"use client";

import type React from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import { toast } from "sonner";
import { useActivityAccount } from "@/hooks/useActivityAccount";
import { useLanguage } from "@/hooks/useLanguage";
import { createLogAdapter } from "@/lib/logger/console-adapter";
import { callActivityApi } from "@/lib/utils/activity-api-client";
import {
	canJoinActivity as canActuallyJoinActivity,
	enrichCampaignWithActualStatus,
	filterCampaignsByActualStatus,
	getActualActivityStatus,
	getStatusColor as getActualStatusColor,
	getStatusText as getActualStatusText,
} from "@/lib/utils/activity-utils";
import {
	type ActivityApiResponse,
	type ActivityCampaign,
	type ActivityCategory,
	type ActivityListQuery,
	ActivityStatus,
	type CreateActivityRequest,
	type PaginatedResponse,
	type UpdateActivityRequest,
} from "@/types/activity";
import { useIdentity } from "./IdentityContext";
import { useVip } from "./VipContext";

interface ActivityContextValue {
	// 状态
	categories: ActivityCategory[];
	campaigns: ActivityCampaign[];
	myCampaigns: ActivityCampaign[];
	isLoading: boolean;
	error: string | null;

	// 分页
	currentPage: number;
	totalPages: number;
	pageSize: number;
	total: number;

	// 方法
	loadCategories: () => Promise<void>;
	loadCampaigns: (query?: ActivityListQuery) => Promise<void>;
	loadMyCampaigns: () => Promise<void>;
	getCampaign: (id: string) => Promise<ActivityCampaign | null>;
	createCampaign: (
		data: CreateActivityRequest,
	) => Promise<ActivityApiResponse<ActivityCampaign>>;
	updateCampaign: (
		id: string,
		data: UpdateActivityRequest,
	) => Promise<ActivityApiResponse<ActivityCampaign>>;
	deleteCampaign: (id: string) => Promise<ActivityApiResponse>;
	joinCampaign: (id: string) => Promise<ActivityApiResponse>;
	startCampaign: (id: string) => Promise<ActivityApiResponse>;
	endCampaign: (id: string) => Promise<ActivityApiResponse>;
	cancelCampaign: (id: string) => Promise<ActivityApiResponse>;

	// 权限检查
	canCreateActivity: (categoryKey: string) => boolean;
	canEditActivity: (campaign: ActivityCampaign) => boolean;
	canJoinActivity: (campaign: ActivityCampaign) => boolean;
	canManageAllActivities: () => boolean;
	canManageActivityAccounts: () => boolean;

	// 工具方法
	getStatusColor: (campaign: ActivityCampaign) => string;
	getStatusText: (campaign: ActivityCampaign) => string;
	getActualStatus: (campaign: ActivityCampaign) => ActivityStatus;
	formatTimeRemaining: (endTime: Date) => string;
}

const ActivityContext = createContext<ActivityContextValue | undefined>(
	undefined,
);
const uiLog = createLogAdapter("contexts/ActivityContext.tsx");

export function ActivityProvider({ children }: { children: React.ReactNode }) {
	const { identity, principal, isAuthenticated } = useIdentity();
	const { vipInfo, checkAccess } = useVip();
	const { hasActivityAccount, hasSuperAccount, hasPermission } =
		useActivityAccount();
	const { language } = useLanguage();

	const [categories, setCategories] = useState<ActivityCategory[]>([]);
	const [campaigns, setCampaigns] = useState<ActivityCampaign[]>([]);
	const [myCampaigns, setMyCampaigns] = useState<ActivityCampaign[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// 分页状态
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [pageSize] = useState(12);
	const [total, setTotal] = useState(0);

	// 加载活动类别
	const loadCategories = useCallback(async () => {
		try {
			const response = await fetch("/api/activities/categories");
			const data = await response.json();

			if (data.success) {
				setCategories(data.data);
			} else {
				throw new Error(data.error || "Failed to load categories");
			}
		} catch (error) {
			uiLog.error("Load categories error:", error);
			setError(error instanceof Error ? error.message : "Unknown error");
		}
	}, []);

	// 加载活动列表
	const loadCampaigns = useCallback(async (query: ActivityListQuery = {}) => {
		setIsLoading(true);
		setError(null);

		try {
			const params = new URLSearchParams();
			Object.entries(query).forEach(([key, value]) => {
				if (value !== undefined && value !== null) {
					if (Array.isArray(value)) {
						value.forEach((v) => params.append(key, String(v)));
					} else {
						params.append(key, String(value));
					}
				}
			});

			const response = await fetch(`/api/activities/list?${params}`);
			const data = await response.json();

			if (data.success) {
				const paginatedData = data.data as PaginatedResponse<ActivityCampaign>;
				setCampaigns(paginatedData.items);
				setCurrentPage(paginatedData.page);
				setTotalPages(paginatedData.totalPages);
				setTotal(paginatedData.total);
			} else {
				throw new Error(data.error || "Failed to load campaigns");
			}
		} catch (error) {
			uiLog.error("Load campaigns error:", error);
			setError(error instanceof Error ? error.message : "Unknown error");
			setCampaigns([]);
		} finally {
			setIsLoading(false);
		}
	}, []);

	// 加载我的活动
	const loadMyCampaigns = useCallback(async () => {
		if (!isAuthenticated || !principal) return;

		try {
			const response = await fetch("/api/activities/my", {
				headers: {
					"X-User-Principal": principal,
				},
			});
			const data = await response.json();

			if (data.success) {
				setMyCampaigns(data.data);
			}
		} catch (error) {
			uiLog.error("Load my campaigns error:", error);
		}
	}, [isAuthenticated, principal]);

	// 获取单个活动
	const getCampaign = useCallback(
		async (id: string): Promise<ActivityCampaign | null> => {
			try {
				const response = await fetch(`/api/activities/${id}`);
				const data = await response.json();

				if (data.success) {
					return data.data;
				}
				return null;
			} catch (error) {
				uiLog.error("Get campaign error:", error);
				return null;
			}
		},
		[],
	);

	// 创建活动
	const createCampaign = useCallback(
		async (
			data: CreateActivityRequest,
		): Promise<ActivityApiResponse<ActivityCampaign>> => {
			if (!isAuthenticated || !identity || !principal) {
				return { success: false, error: "请先登录" };
			}

			try {
				const result = await callActivityApi<ActivityCampaign>(
					identity,
					principal,
					"POST",
					"/api/activities/create",
					data,
				);

				if (result.success) {
					toast.success(
						language === "zh"
							? "活动创建成功"
							: "Activity created successfully",
					);
					await loadMyCampaigns();
				} else {
					toast.error(
						result.error ||
							(language === "zh" ? "创建失败" : "Creation failed"),
					);
				}

				return result;
			} catch (error) {
				const errorMessage =
					error instanceof Error ? error.message : "Unknown error";
				toast.error(errorMessage);
				return { success: false, error: errorMessage };
			}
		},
		[isAuthenticated, identity, principal, language],
	);

	// 更新活动
	const updateCampaign = useCallback(
		async (
			id: string,
			data: UpdateActivityRequest,
		): Promise<ActivityApiResponse<ActivityCampaign>> => {
			if (!isAuthenticated || !identity || !principal) {
				return { success: false, error: "请先登录" };
			}

			try {
				const result = await callActivityApi<ActivityCampaign>(
					identity,
					principal,
					"PUT",
					`/api/activities/${id}`,
					data,
				);

				if (result.success) {
					toast.success(
						language === "zh"
							? "活动更新成功"
							: "Activity updated successfully",
					);
					await loadMyCampaigns();
				} else {
					toast.error(
						result.error || (language === "zh" ? "更新失败" : "Update failed"),
					);
				}

				return result;
			} catch (error) {
				const errorMessage =
					error instanceof Error ? error.message : "Unknown error";
				toast.error(errorMessage);
				return { success: false, error: errorMessage };
			}
		},
		[isAuthenticated, identity, principal, language],
	);

	// 删除活动
	const deleteCampaign = useCallback(
		async (id: string): Promise<ActivityApiResponse> => {
			if (!isAuthenticated || !identity || !principal) {
				return { success: false, error: "请先登录" };
			}

			try {
				const result = await callActivityApi(
					identity,
					principal,
					"DELETE",
					`/api/activities/${id}`,
				);

				if (result.success) {
					toast.success(language === "zh" ? "活动已删除" : "Activity deleted");
					await loadMyCampaigns();
				} else {
					toast.error(
						result.error || (language === "zh" ? "删除失败" : "Delete failed"),
					);
				}

				return result;
			} catch (error) {
				const errorMessage =
					error instanceof Error ? error.message : "Unknown error";
				toast.error(errorMessage);
				return { success: false, error: errorMessage };
			}
		},
		[isAuthenticated, identity, principal, language],
	);

	// 参加活动
	const joinCampaign = useCallback(
		async (id: string): Promise<ActivityApiResponse> => {
			if (!isAuthenticated || !identity || !principal) {
				return { success: false, error: "请先登录" };
			}

			try {
				const result = await callActivityApi(
					identity,
					principal,
					"POST",
					`/api/activities/${id}/join`,
				);

				if (result.success) {
					toast.success(
						language === "zh" ? "成功参加活动" : "Successfully joined activity",
					);
				} else {
					toast.error(
						result.error || (language === "zh" ? "参加失败" : "Join failed"),
					);
				}

				return result;
			} catch (error) {
				const errorMessage =
					error instanceof Error ? error.message : "Unknown error";
				toast.error(errorMessage);
				return { success: false, error: errorMessage };
			}
		},
		[isAuthenticated, identity, principal, language],
	);

	// 启动活动
	const startCampaign = useCallback(
		async (id: string): Promise<ActivityApiResponse> => {
			if (!isAuthenticated || !identity || !principal) {
				return { success: false, error: "请先登录" };
			}

			try {
				const result = await callActivityApi(
					identity,
					principal,
					"POST",
					`/api/activities/${id}/start`,
				);

				if (result.success) {
					toast.success(language === "zh" ? "活动已启动" : "Activity started");
					await loadMyCampaigns();
				} else {
					toast.error(
						result.error || (language === "zh" ? "启动失败" : "Start failed"),
					);
				}

				return result;
			} catch (error) {
				const errorMessage =
					error instanceof Error ? error.message : "Unknown error";
				toast.error(errorMessage);
				return { success: false, error: errorMessage };
			}
		},
		[isAuthenticated, identity, principal, language],
	);

	// 结束活动
	const endCampaign = useCallback(
		async (id: string): Promise<ActivityApiResponse> => {
			if (!isAuthenticated || !identity || !principal) {
				return { success: false, error: "请先登录" };
			}

			try {
				const result = await callActivityApi(
					identity,
					principal,
					"POST",
					`/api/activities/${id}/end`,
				);

				if (result.success) {
					toast.success(language === "zh" ? "活动已结束" : "Activity ended");
					await loadMyCampaigns();
				} else {
					toast.error(
						result.error || (language === "zh" ? "结束失败" : "End failed"),
					);
				}

				return result;
			} catch (error) {
				const errorMessage =
					error instanceof Error ? error.message : "Unknown error";
				toast.error(errorMessage);
				return { success: false, error: errorMessage };
			}
		},
		[isAuthenticated, identity, principal, language],
	);

	// 取消活动
	const cancelCampaign = useCallback(
		async (id: string): Promise<ActivityApiResponse> => {
			if (!isAuthenticated || !identity || !principal) {
				return { success: false, error: "请先登录" };
			}

			try {
				const result = await callActivityApi(
					identity,
					principal,
					"PUT",
					`/api/activities/${id}`,
					{ status: "cancelled" },
				);

				if (result.success) {
					toast.success(
						language === "zh" ? "活动已取消" : "Activity cancelled",
					);
					await loadMyCampaigns();
				} else {
					toast.error(
						result.error || (language === "zh" ? "取消失败" : "Cancel failed"),
					);
				}

				return result;
			} catch (error) {
				const errorMessage =
					error instanceof Error ? error.message : "Unknown error";
				toast.error(errorMessage);
				return { success: false, error: errorMessage };
			}
		},
		[isAuthenticated, identity, principal, language],
	);

	// 权限检查
	const canCreateActivity = useCallback(
		(categoryKey: string): boolean => {
			if (!isAuthenticated) return false;

			// 只允许活动账号和超级账号创建活动
			// 移除VIP权限检查
			return hasActivityAccount;
		},
		[isAuthenticated, hasActivityAccount],
	);

	const canEditActivity = useCallback(
		(campaign: ActivityCampaign): boolean => {
			if (!isAuthenticated || !principal) return false;

			// 创建者可以编辑自己的活动
			if (campaign.creatorPrincipal === principal) return true;

			// 超级账号可以编辑所有活动
			return hasSuperAccount;
		},
		[isAuthenticated, principal, hasSuperAccount],
	);

	// 新增：检查是否可以管理所有活动
	const canManageAllActivities = useCallback((): boolean => {
		return isAuthenticated && hasSuperAccount;
	}, [isAuthenticated, hasSuperAccount]);

	// 新增：检查是否可以管理活动账号
	const canManageActivityAccounts = useCallback((): boolean => {
		return isAuthenticated && hasSuperAccount;
	}, [isAuthenticated, hasSuperAccount]);

	const canJoinActivity = useCallback(
		(campaign: ActivityCampaign): boolean => {
			if (!isAuthenticated) return false;
			const actualStatus = getActualActivityStatus(campaign);
			if (actualStatus !== ActivityStatus.ACTIVE) return false;
			if (campaign.isParticipant) return false;
			if (
				campaign.maxParticipants &&
				campaign.currentParticipants >= campaign.maxParticipants
			)
				return false;
			if (vipInfo.level < campaign.minVipLevel) return false;

			return true;
		},
		[isAuthenticated, vipInfo],
	);

	// 工具方法 - 使用智能状态判断
	const getStatusColor = useCallback((campaign: ActivityCampaign): string => {
		const actualStatus = getActualActivityStatus(campaign);
		return getActualStatusColor(actualStatus);
	}, []);

	const getStatusText = useCallback(
		(campaign: ActivityCampaign): string => {
			const actualStatus = getActualActivityStatus(campaign);
			return getActualStatusText(actualStatus, language as "en" | "zh");
		},
		[language],
	);

	// 获取活动的实际状态（用于外部调用）
	const getActualStatus = useCallback(
		(campaign: ActivityCampaign): ActivityStatus => {
			return getActualActivityStatus(campaign);
		},
		[],
	);

	const formatTimeRemaining = useCallback(
		(endTime: Date): string => {
			const now = new Date();
			const end = new Date(endTime);
			const diff = end.getTime() - now.getTime();

			if (diff <= 0) {
				return language === "zh" ? "已结束" : "Ended";
			}

			const days = Math.floor(diff / (1000 * 60 * 60 * 24));
			const hours = Math.floor(
				(diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
			);
			const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

			if (days > 0) {
				return language === "zh"
					? `${days}天${hours}小时`
					: `${days}d ${hours}h`;
			} else if (hours > 0) {
				return language === "zh"
					? `${hours}小时${minutes}分钟`
					: `${hours}h ${minutes}m`;
			} else {
				return language === "zh" ? `${minutes}分钟` : `${minutes}m`;
			}
		},
		[language],
	);

	// 初始化加载类别
	useEffect(() => {
		loadCategories();
	}, [loadCategories]);

	// 登录状态变化时加载我的活动
	useEffect(() => {
		if (isAuthenticated) {
			loadMyCampaigns();
		} else {
			setMyCampaigns([]);
		}
	}, [isAuthenticated, loadMyCampaigns]);

	const value: ActivityContextValue = {
		categories,
		campaigns,
		myCampaigns,
		isLoading,
		error,
		currentPage,
		totalPages,
		pageSize,
		total,
		loadCategories,
		loadCampaigns,
		loadMyCampaigns,
		getCampaign,
		createCampaign,
		updateCampaign,
		deleteCampaign,
		joinCampaign,
		startCampaign,
		endCampaign,
		cancelCampaign,
		canCreateActivity,
		canEditActivity,
		canJoinActivity,
		canManageAllActivities,
		canManageActivityAccounts,
		getStatusColor,
		getStatusText,
		getActualStatus,
		formatTimeRemaining,
	};

	return (
		<ActivityContext.Provider value={value}>
			{children}
		</ActivityContext.Provider>
	);
}

export function useActivity() {
	const context = useContext(ActivityContext);
	if (context === undefined) {
		throw new Error("useActivity must be used within an ActivityProvider");
	}
	return context;
}
